import { NavLink, useNavigate } from "react-router-dom";
import { useAuth, authFetch } from "../context/AuthContext";
import { Button } from "./ui/button";
import { NotificationBell } from "./NotificationBell";
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Textarea } from "./ui/textarea";
import { 
  LayoutDashboard, 
  Shirt, 
  Camera, 
  TrendingUp, 
  Target,
  FileText,
  Gift,
  Settings, 
  LogOut,
  Menu,
  X,
  Plus,
  HelpCircle,
  Send,
  Shield
} from "lucide-react";
import { useState, useEffect } from "react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Super admin emails (client-side check for UI only - real check is on backend)
const ADMIN_EMAILS = ["fordhoogendoorn@gmail.com", "concordehouse01@gmail.com"];

const navItems = [
  { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
  { to: "/wardrobe", icon: Shirt, label: "Wardrobe" },
  { to: "/daily-log", icon: Camera, label: "Daily Log" },
  { to: "/insights", icon: TrendingUp, label: "Insights" },
  { to: "/smart-sell", icon: Target, label: "Smart Sell" },
  { to: "/reports", icon: FileText, label: "Reports" },
  { to: "/referral", icon: Gift, label: "Invite" },
  { to: "/settings", icon: Settings, label: "Settings" },
];

// Mobile bottom nav - only show most important items
const mobileNavItems = [
  { to: "/dashboard", icon: LayoutDashboard, label: "Home" },
  { to: "/wardrobe", icon: Shirt, label: "Wardrobe" },
  { to: "/daily-log", icon: Camera, label: "Log", highlight: true },
  { to: "/insights", icon: TrendingUp, label: "Insights" },
  { to: "#more", icon: Menu, label: "More" }, // Special handler, not a real route
];

export function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [helpModalOpen, setHelpModalOpen] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState("");
  const [sendingFeedback, setSendingFeedback] = useState(false);
  
  // Check if user is admin (client-side for UI, backend validates actual access)
  const isAdmin = user?.email && ADMIN_EMAILS.some(e => e.toLowerCase() === user.email.toLowerCase());

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const handleSendFeedback = async () => {
    if (!feedbackMessage.trim()) {
      toast.error("Please enter a message");
      return;
    }
    
    setSendingFeedback(true);
    try {
      const response = await authFetch(`${API_URL}/api/feedback`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: feedbackMessage }),
      });
      
      if (response.ok) {
        toast.success("Feedback sent! Thank you.");
        setFeedbackMessage("");
        setHelpModalOpen(false);
      } else {
        throw new Error("Failed to send");
      }
    } catch (error) {
      console.error("Feedback error:", error);
      toast.error("Failed to send feedback. Please try again.");
    } finally {
      setSendingFeedback(false);
    }
  };

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-0">
      {/* Top Navigation - Fixed header with safe area padding */}
      <header className="fixed top-0 left-0 right-0 z-50 w-full border-b border-border/50 bg-background/95 backdrop-blur-md pt-[env(safe-area-inset-top,0px)]">
        <div className="container mx-auto px-4 md:px-8 lg:px-12">
          <div className="flex h-14 md:h-16 items-center justify-between">
            {/* Logo */}
            <NavLink to="/dashboard" className="flex items-center">
              <span className="font-heading text-lg tracking-tight">WornWell</span>
            </NavLink>

            {/* Desktop Navigation - Subtle */}
            <nav className="hidden md:flex items-center gap-1">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    `flex items-center gap-2 px-3 py-2 text-sm transition-colors rounded-md ${
                      isActive
                        ? "text-primary font-medium"
                        : "text-muted-foreground hover:text-primary"
                    }`
                  }
                  data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                >
                  <item.icon className="w-4 h-4" />
                  {item.label}
                </NavLink>
              ))}
            </nav>

            {/* User Menu */}
            <div className="flex items-center gap-2 md:gap-4">
              {/* Admin Button (only for admins) */}
              {isAdmin && (
                <NavLink
                  to="/admin"
                  className={({ isActive }) =>
                    `flex items-center gap-1.5 px-2 py-1.5 text-sm rounded-md transition-colors ${
                      isActive
                        ? "text-terracotta-500 bg-terracotta-500/10"
                        : "text-muted-foreground hover:text-primary"
                    }`
                  }
                  data-testid="admin-link"
                >
                  <Shield className="w-4 h-4" />
                  <span className="hidden md:inline">Admin</span>
                </NavLink>
              )}
              
              {/* Help Button */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setHelpModalOpen(true)}
                className="flex items-center gap-1.5 text-muted-foreground hover:text-primary"
                data-testid="help-btn"
              >
                <HelpCircle className="w-4 h-4" />
                <span className="hidden md:inline text-sm">Help</span>
              </Button>
              
              {/* Notification Bell */}
              <NotificationBell />
              
              <div className="hidden md:flex items-center gap-3">
                {user?.picture && (
                  <img
                    src={user.picture}
                    alt={user.name}
                    className="w-8 h-8 rounded-full object-cover"
                  />
                )}
                <span className="text-sm font-medium">{user?.name}</span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="hidden md:flex items-center gap-2"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4" />
                Logout
              </Button>

              {/* Mobile: User Avatar */}
              <div className="md:hidden">
                {user?.picture && (
                  <img
                    src={user.picture}
                    alt={user.name}
                    className="w-8 h-8 rounded-full object-cover"
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Slide-out Menu (for "More" items) */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-[55] md:hidden">
          <div className="fixed inset-0 bg-black/20" onClick={() => setMobileMenuOpen(false)} />
          <nav className="fixed bottom-[calc(4rem+env(safe-area-inset-bottom,0px))] right-0 left-0 bg-background border-t border-border p-4 rounded-t-2xl shadow-lg animate-in slide-in-from-bottom duration-200 z-[56]">
            <div className="w-12 h-1 bg-border rounded-full mx-auto mb-4" />
            <div className="grid grid-cols-4 gap-2">
              {navItems.filter(item => !mobileNavItems.some(m => m.to === item.to)).map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) =>
                    `flex flex-col items-center gap-1 p-3 text-xs font-medium transition-colors rounded-lg ${
                      isActive
                        ? "bg-sand-100 text-primary"
                        : "text-muted-foreground active:bg-sand-50"
                    }`
                  }
                >
                  <item.icon className="w-5 h-5" />
                  {item.label}
                </NavLink>
              ))}
              <button
                onClick={() => {
                  setMobileMenuOpen(false);
                  handleLogout();
                }}
                className="flex flex-col items-center gap-1 p-3 text-xs font-medium text-muted-foreground active:bg-sand-50 rounded-lg"
              >
                <LogOut className="w-5 h-5" />
                Logout
              </button>
            </div>
          </nav>
        </div>
      )}

      {/* Main Content - Clears fixed header (safe-area + h-14) */}
      <main className="container mx-auto px-4 md:px-8 lg:px-12 pt-[calc(env(safe-area-inset-top,0px)+4rem)] md:pt-[calc(env(safe-area-inset-top,0px)+5rem)] pb-8 md:pb-12">
        {children}
      </main>

      {/* Mobile Bottom Navigation - Clean */}
      <nav className="fixed bottom-0 left-0 right-0 z-50 md:hidden bg-background/98 backdrop-blur-sm border-t border-border/50 safe-area-inset-bottom">
        <div className="flex items-center justify-around h-16">
          {mobileNavItems.map((item) => {
            if (item.label === "More") {
              return (
                <button
                  key="more"
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  className={`flex flex-col items-center gap-0.5 px-3 py-2 text-[10px] font-medium transition-colors ${
                    mobileMenuOpen ? "text-primary" : "text-muted-foreground"
                  }`}
                  data-testid="mobile-more-btn"
                >
                  <Menu className="w-5 h-5" />
                  <span>More</span>
                </button>
              );
            }
            
            return (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setMobileMenuOpen(false)}
                className={({ isActive }) =>
                  `flex flex-col items-center gap-0.5 px-3 py-2 text-[10px] font-medium transition-colors ${
                    item.highlight
                      ? isActive
                        ? "text-terracotta-500"
                        : "text-terracotta-400"
                      : isActive
                        ? "text-primary"
                        : "text-muted-foreground"
                  }`
                }
                data-testid={`mobile-nav-${item.label.toLowerCase()}`}
              >
                {item.highlight ? (
                  <div className="w-10 h-10 -mt-4 bg-terracotta-500 rounded-full flex items-center justify-center shadow-lg">
                    <item.icon className="w-5 h-5 text-white" />
                  </div>
                ) : (
                  <item.icon className="w-5 h-5" />
                )}
                <span>{item.label}</span>
              </NavLink>
            );
          })}
        </div>
      </nav>

      {/* Help/Feedback Modal */}
      <Dialog open={helpModalOpen} onOpenChange={setHelpModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <HelpCircle className="w-5 h-5 text-terracotta-500" />
              Help & Feedback
            </DialogTitle>
            <DialogDescription>
              Found a bug or have a suggestion? Let us know and we'll get back to you.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <Textarea
              placeholder="Describe the issue or share your feedback..."
              value={feedbackMessage}
              onChange={(e) => setFeedbackMessage(e.target.value)}
              className="min-h-[120px] resize-none"
              data-testid="feedback-textarea"
            />
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setHelpModalOpen(false);
                  setFeedbackMessage("");
                }}
                data-testid="feedback-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSendFeedback}
                disabled={sendingFeedback || !feedbackMessage.trim()}
                className="bg-terracotta-500 hover:bg-terracotta-600 text-white"
                data-testid="feedback-send-btn"
              >
                {sendingFeedback ? (
                  <>Sending...</>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send Feedback
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
